import { Button } from '@acpaas-ui/react-components';
import { ATTACHMENT_TYPES } from '../../../constants/settings.constants';
import { TEMPLATE_CATEGORIES_SUFFIXES } from '../../../constants/templates.constants';
import DossierTemplates from '../../../containers/dossier-templates/DossierTemplates';
import dateValidators from '../../../form-validators/date.validators';
import { useAttachmentCRUD } from '../../../hooks/useAttachmentCRUD.hook';
import { useForm } from '../../../hooks/useForm.hook';
import { useResourceInput } from '../../../hooks/useResourceInput';
import { getAttachmentsForProcedurePart } from '../../../lib/dossier.utils';
import { getTemplateCategory } from '../../../lib/template.utils';
import AttachmentDetailsModal from '../../../modals/AttachmentDetailsModal';
import AttachmentUploadModal from '../../../modals/AttachmentUploadModal';
import DeleteWarningModal from '../../../modals/DeleteWarningModal';
import procedureService from '../../../services/api/procedure.service';
import AttachmentsList from '../../attachments/AttachmentsList';
import Datepicker from '../../forms/Datepicker';
import TextArea from '../../forms/TextArea';
import TextField from '../../forms/TextField';

const DossierProcedurePart = ({
  dossierId,
  dossierType,
  id,
  name,
  startDate,
  endDate,
  note,
  attachments,
  onDelete,
  onUpdated,
  onAttachmentsRefetch,
  isLoadingAttachments,
  write
}) => {
  const attachmentCRUD = useAttachmentCRUD(dossierId);
  const { isSubmitting, data, reset, input, submitStart, submitOk, submitError } = useForm({
    id,
    name,
    startDate,
    endDate,
    note
  });
  const resource = {
    isFetching: false,
    isSubmitting,
    getValue: (field) => data?.[field],
    setValue: (value, field, options = {}) => {
      submitStart();
      procedureService.updatePart(
        dossierId,
        data?.id,
        { [field]: value },
        {
          onSuccess: () => {
            submitOk();
            input(value, field);
            onUpdated(value, field, data?.id);
            options.onSuccess();
          },
          onError: (message) => {
            submitError({ [field]: message });
            options.onError(message);
          }
        }
      );
    },
    validators: {
      startDate: (value) => dateValidators.lteNonRequired(value, endDate),
      endDate: (value) => dateValidators.gteNonRequired(value, startDate)
    },
    onCancel: reset
  };

  return (
    <div className="spo-m-dossier-procedure-part">
      <div className="row">
        <div className="col-xs-12 col-md-6 col-lg-4 u-margin-bottom-xs">
          <TextField label="Naam onderdeel" {...useResourceInput('name', resource)} readOnly={!write} />
        </div>
        {write && (
          <Button
            className="u-margin-left-auto u-margin-top-label u-margin-bottom-auto"
            icon="ai-bin"
            type="danger"
            transparent
            onClick={onDelete}
          />
        )}
      </div>
      <div className="row">
        <div className="col-xs-12 col-md-6 col-lg-4 u-margin-bottom-xs">
          <Datepicker
            label="Startdatum"
            {...useResourceInput('startDate', resource, '', { ignoreTouched: true })}
            maxDate={data?.endDate}
            readOnly={!write}
          />
        </div>
        <div className="col-xs-12 col-md-6 col-lg-4 u-margin-bottom-xs">
          <Datepicker
            label="Einddatum"
            {...useResourceInput('endDate', resource, '', { ignoreTouched: true })}
            minDate={data?.startDate}
            readOnly={!write}
          />
        </div>
      </div>
      <div className="row u-margin-bottom-md">
        <div className="col-xs-12">
          <TextArea label="Nota" size="small" {...useResourceInput('note', resource)} readOnly={!write} />
        </div>
      </div>
      <AttachmentsList
        loading={isLoadingAttachments}
        attachments={getAttachmentsForProcedurePart(data, attachments)}
        title="Bestanden"
        onUpload={
          write ? () => attachmentCRUD.onAttachmentAddOpen(data?.id, ATTACHMENT_TYPES.PROCEDURE_PART) : undefined
        }
        onOpen={attachmentCRUD.onAttachmentOpen}
        templates={
          write && (
            <DossierTemplates
              category={getTemplateCategory(dossierType, TEMPLATE_CATEGORIES_SUFFIXES.PROCEDURE_COMPONENTS)}
            />
          )
        }
      />
      <AttachmentUploadModal {...attachmentCRUD.uploadModal} onConfirm={attachmentCRUD.onAttachmentAdd} />
      <AttachmentDetailsModal
        {...attachmentCRUD.detailsModal}
        write={write}
        form={attachmentCRUD.attachmentDetailForm}
        dossierId={dossierId}
        onUpdate={attachmentCRUD.onAttachmentUpdate}
        onUpdateSuccess={onAttachmentsRefetch}
        onDelete={attachmentCRUD.deleteModal.onOpen}
      />
      {attachmentCRUD.deleteModal.isOpen && (
        <DeleteWarningModal
          {...attachmentCRUD.deleteModal}
          warningText="Weet je zeker dat je deze bijlage wilt verwijderen?"
          onConfirm={attachmentCRUD.onAttachmentDelete}
        />
      )}
    </div>
  );
};

export default DossierProcedurePart;
